import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, FileTextIcon, Trash2Icon, UserIcon, ChevronDownIcon, MailCheckIcon, MinusCircleIcon, UploadIcon, ClipboardPenIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { Condition, Document, ConditionStatus } from '../types';
interface ConditionRowProps {
  condition: Condition;
  linkedDocument?: Document;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onStatusChange: (status: ConditionStatus) => void;
  onRemove: () => void;
}
const formatDate = (date?: Date) => {
  if (!date) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
const STATUS_CONFIG: Record<ConditionStatus, {
  label: string;
  bg: string;
  text: string;
  border: string;
  dot: string;
}> = {
  new: {
    label: 'New',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    dot: 'bg-blue-500'
  },
  requested: {
    label: 'Requested',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  review: {
    label: 'Review',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    dot: 'bg-purple-500'
  },
  cleared: {
    label: 'Cleared',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  }
};
const ALL_STATUSES: ConditionStatus[] = ['new', 'requested', 'review', 'cleared'];
export function ConditionRow({
  condition,
  linkedDocument,
  isSelected,
  onToggleSelect,
  onStatusChange,
  onRemove
}: ConditionRowProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Also close on Escape
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setStatusDropdownOpen(false);
      };
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKey);
      };
    }
  }, [statusDropdownOpen]);
  const currentStatus = STATUS_CONFIG[condition.status];
  return <motion.div initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -4
  }} className={`group flex items-center py-3 px-4 border-b border-slate-100 transition-colors ${isSelected ? 'bg-blue-50/50' : 'bg-white hover:bg-slate-50/70'}`} data-id="element-113">
      {/* Checkbox */}
      <button onClick={onToggleSelect} className="w-8 flex-shrink-0 flex justify-center focus:outline-none" data-id="element-114">
        <div className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white group-hover:border-blue-400'}`} data-id="element-115">
          {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-116" />}
        </div>
      </button>

      {/* Name & Description */}
      <div className="flex-1 min-w-0 pr-4" data-id="element-117">
        <p className={`text-sm font-semibold ${condition.status === 'cleared' ? 'text-slate-500 line-through' : 'text-slate-800'}`} data-id="element-118">
          {condition.name}
        </p>
        {condition.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed" data-id="element-119">
            {condition.description}
          </p>}
        {linkedDocument && <div className="flex items-center gap-2 mt-1.5" data-id="element-120">
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[10px] font-medium" data-id="element-121">
              <FileTextIcon className="w-3 h-3" data-id="element-122" />
              <span className="truncate max-w-[120px]" data-id="element-123">
                {linkedDocument.name}
              </span>
            </div>
          </div>}
      </div>

      {/* Status — clickable dropdown */}
      <div className="w-28 flex-shrink-0 flex justify-center relative" ref={dropdownRef} data-id="element-124">
        <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider border cursor-pointer transition-all hover:shadow-sm ${currentStatus.bg} ${currentStatus.text} ${currentStatus.border}`} title="Click to change status" data-id="element-125">
          <span data-id="element-126">{currentStatus.label}</span>
          <ChevronDownIcon className={`w-3 h-3 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} data-id="element-127" />
        </button>

        {statusDropdownOpen && <div className="absolute top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px] left-1/2 -translate-x-1/2" data-id="element-128">
            {ALL_STATUSES.map(status => {
          const config = STATUS_CONFIG[status];
          const isActive = condition.status === status;
          return <button key={status} onClick={() => {
            onStatusChange(status);
            setStatusDropdownOpen(false);
          }} className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center gap-2.5 transition-colors ${isActive ? 'bg-slate-50' : 'hover:bg-slate-50'}`} data-id="element-129">
                  <div className={`w-2.5 h-2.5 rounded-full ${config.dot}`} data-id="element-130" />
                  <span className={`uppercase tracking-wider font-bold ${config.text}`} data-id="element-131">
                    {config.label}
                  </span>
                  {isActive && <CheckCircle2Icon className="w-3.5 h-3.5 text-slate-400 ml-auto" data-id="element-132" />}
                </button>;
        })}
          </div>}
      </div>

      {/* Created */}
      <div className="w-24 flex-shrink-0 text-center" data-id="element-133">
        <div className="text-xs text-slate-700" data-id="element-134">
          {formatDate(condition.createdAt)}
        </div>
        <div className="text-[10px] text-slate-400" data-id="element-135">Created</div>
      </div>

      {/* Requested */}
      <div className="w-24 flex-shrink-0 text-center" data-id="element-136">
        <div className="text-xs text-slate-700" data-id="element-137">
          {formatDate(condition.requestedAt)}
        </div>
        {condition.requestedAt && <div className="text-[10px] text-slate-400" data-id="element-138">Requested</div>}
      </div>

      {/* Assigned To */}
      <div className="w-32 flex-shrink-0 flex flex-col items-center justify-center" data-id="element-139">
        {condition.assignedTo ? <>
            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 mb-1" data-id="element-140">
              <UserIcon className="w-3.5 h-3.5" data-id="element-141" />
            </div>
            <span className="text-[10px] text-slate-500 text-center truncate w-full px-2" data-id="element-142">
              {condition.assignedTo}
            </span>
          </> : <span className="text-xs text-slate-400" data-id="element-143">-</span>}
      </div>

      {/* Actions */}
      <div className="w-56 flex-shrink-0 flex items-center justify-end gap-1.5 pr-2" data-id="element-144">
        <button onClick={() => onStatusChange('cleared')} className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Approve / Clear" aria-label="Approve condition" data-id="element-145">
          <MailCheckIcon className="w-4 h-4" data-id="element-146" />
        </button>
        <button onClick={() => onStatusChange('new')} className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Reject / Deny" aria-label="Reject condition" data-id="element-147">
          <MinusCircleIcon className="w-4 h-4" data-id="element-148" />
        </button>

        <div className="w-px h-5 bg-slate-200" data-id="element-149" />

        <button className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Upload Document" aria-label="Upload document" data-id="element-150">
          <UploadIcon className="w-4 h-4" data-id="element-151" />
        </button>
        <button className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Edit Condition" aria-label="Edit condition" data-id="element-152">
          <ClipboardPenIcon className="w-4 h-4" data-id="element-153" />
        </button>
        <button className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Add Comment" aria-label="Add comment" data-id="element-154">
          <MessageSquareIcon className="w-4 h-4" data-id="element-155" />
        </button>
        <button onClick={onRemove} className="w-8 h-8 rounded-full bg-[#7B2D3B] flex items-center justify-center text-white hover:bg-[#5E1F2C] active:scale-95 transition-all shadow-sm" title="Delete Condition" aria-label="Delete condition" data-id="element-156">
          <Trash2Icon className="w-4 h-4" data-id="element-157" />
        </button>
      </div>
    </motion.div>;
}